import React from 'react';
import { useGuestUnlockInfoController } from '../UnlockDialog/UnlockDialog.controller';
import { DialogStyled } from '../DialogStyled/DialogStyled';
import type { UnlockDialogContextValue } from './UnlockDialog.context';
import { useUnlockDialogContext } from './UnlockDialog.context';
import type { ButtonV2Props } from '@withjoy/joykit';
import { ButtonV2, DrawerV2, Flex } from '@withjoy/joykit';
import { ManageUnlockDialogForms } from '@apps/guest/packages/layout-engine/components/UnlockDialog/ManageUnlockDialogForms/ManageUnlockDialogForms';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';

export interface GuestTypes {
  firstName: string | null | undefined;
  lastName: string | null | undefined;
  personIdTag?: string;
}

export interface ButtonV2PropsExt extends ButtonV2Props {
  shouldDisplayUnlockButton: Maybe<boolean>;
  unlockButtonText: string;
}

const UnlockDialog: React.FC<UnlockDialogContextValue & { shouldSkipPasswordGate: boolean }> & { TriggerButton: typeof TriggerButton } = ({
  isOpen,
  onClose,
  shouldDisplayFindInvite,
  shouldSkipPasswordGate,
}) => {
  const unlockData = useGuestUnlockInfoController();
  const { showBackButton } = unlockData;
  const isMobile = useIsMobileScreen();

  const formContent = (
    <ManageUnlockDialogForms
      unlockData={{ ...unlockData, shouldDisplayFindInvite, shouldSkipPasswordGate }}
      onDialogClose={onClose}
    />
  );

  if (isMobile) {
    return (
      <DrawerV2
        isOpen={isOpen}
        onClose={onClose}
        anchor="bottom"
        size="auto"
        useBackdrop
        overrides={{
          Content: {
            props: {
              borderTopLeftRadius: '1rem',
              borderTopRightRadius: '1rem',
              height: 'fit-content',
            },
          },
          Body: {
            props: {
              padding: '0',
            },
          },
          Header: {
            props: {
              padding: '0',
            },
          },
        }}
      >
        <DrawerV2.Header>
          <Flex
            width="100%"
            alignItems="center"
          >
            {showBackButton && <DialogStyled.BackButton />}
          </Flex>
        </DrawerV2.Header>
        <DrawerV2.Body>{formContent}</DrawerV2.Body>
      </DrawerV2>
    );
  }

  return (
    <DialogStyled
      isOpen={isOpen}
      onClose={onClose}
    >
      <DialogStyled.Header>
        <DialogStyled.BackButton hidden={!showBackButton} />
        <DialogStyled.CloseButton onClick={onClose} />
      </DialogStyled.Header>
      <DialogStyled.Body>{formContent}</DialogStyled.Body>
    </DialogStyled>
  );
};

const TriggerButton: React.FC<ButtonV2PropsExt> = props => {
  const { handleUnlockDialogOpen } = useUnlockDialogContext();
  const { shouldDisplayUnlockButton, unlockButtonText } = props;

  if (!shouldDisplayUnlockButton) return null;

  return (
    <ButtonV2
      shape="rounded"
      onClick={() => handleUnlockDialogOpen()}
      {...props}
    >
      {unlockButtonText}
    </ButtonV2>
  );
};

UnlockDialog.TriggerButton = TriggerButton;

export { UnlockDialog };
